/** Labra AI (SALES product) feature keys — admin may persist PARTNER_IQ; runtime uses PARTNERIQ. */
export const PARTNER_IQ_FEATURE = 'PARTNERIQ'
export const PARTNER_IQ_ADMIN_FEATURE = 'PARTNER_IQ'
export const PROPENSITY_FEATURE = 'SALES_INTEL'

export const LABRA_AI_ONBOARDING_COMPLETE_KEY =
  'labraAiPartnerIqOnboardingComplete'

export const normalizeSalesFeatureKey = (key: string): string => {
  const upper = key.trim().toUpperCase()
  if (upper === 'PARTNER_IQ' || upper === 'PARTNERIQ') {
    return PARTNER_IQ_FEATURE
  }
  return key
}

export const normalizeSalesFeatures = (features: string[] = []): string[] =>
  Array.from(new Set(features.map(normalizeSalesFeatureKey)))

export const hasPartnerIqFeature = (features: string[] = []): boolean =>
  normalizeSalesFeatures(features).includes(PARTNER_IQ_FEATURE)

export const hasPropensityFeature = (features: string[] = []): boolean => {
  const propensityEnabled = features.includes(PROPENSITY_FEATURE)
  return propensityEnabled
}

/** SALES subscription has PartnerIQ without Propensity (SALES_INTEL). */
export const isPartnerIqOnlySalesSubscription = (subscribedProducts?: {
  SALES?: SalesSubscription
}): boolean =>
  isPartnerIqOrgEnabled(subscribedProducts) &&
  !hasPropensityFeature(subscribedProducts?.SALES?.features ?? [])

/** SALES subscription has Propensity (PTB) without PartnerIQ feature. */
export const isPtbOnlySalesSubscription = (subscribedProducts?: {
  SALES?: SalesSubscription
}): boolean => {
  const features = subscribedProducts?.SALES?.features ?? []
  return hasPropensityFeature(features) && !hasPartnerIqFeature(features)
}

export type SalesSubscription = {
  features?: string[]
  partnerIqEnabled?: boolean
  /** Persisted on SALES subscription when PartnerIQ onboarding is finished. */
  partnerIqOnboardingComplete?: boolean
  cloudProviders?: string[]
  plan?: string
  [key: string]: unknown
}

/** PartnerIQ feature row checked in admin — feature flag and/or partnerIqEnabled. */
export const isPartnerIqFeatureSelectedInAdmin = (
  sales?: SalesSubscription
): boolean => {
  if (!sales) return false
  if (sales.partnerIqEnabled === true) return true
  return hasPartnerIqFeature(sales.features)
}

/** Align SALES.features keys and partnerIqEnabled after identity-service load. */
export const normalizeSalesSubscription = (
  sales?: SalesSubscription
): SalesSubscription | undefined => {
  if (!sales) return sales

  const normalizedFeatures = normalizeSalesFeatures(sales.features ?? [])
  const partnerIqFromFeatures = hasPartnerIqFeature(normalizedFeatures)
  const partnerIqEnabled =
    sales.partnerIqEnabled === false
      ? false
      : sales.partnerIqEnabled === true || partnerIqFromFeatures

  const featuresForUi = Array.from(
    new Set(
      normalizedFeatures.map(f =>
        f === PARTNER_IQ_FEATURE ? PARTNER_IQ_ADMIN_FEATURE : f
      )
    )
  )

  return {
    ...sales,
    features: featuresForUi,
    partnerIqEnabled,
    partnerIqOnboardingComplete:
      sales.partnerIqOnboardingComplete === true ? true : undefined,
  }
}

export const normalizeSubscribedProducts = <
  T extends { SALES?: SalesSubscription; [key: string]: unknown },
>(
  subscribedProducts?: T
): T | undefined => {
  if (!subscribedProducts?.SALES) return subscribedProducts
  return {
    ...subscribedProducts,
    SALES: normalizeSalesSubscription(subscribedProducts.SALES),
  }
}

export const isPartnerIqOrgEnabled = (subscribedProducts?: {
  SALES?: { features?: string[]; partnerIqEnabled?: boolean }
}): boolean => {
  const sales = subscribedProducts?.SALES
  if (!sales) return false
  if (sales.partnerIqEnabled === false) return false
  if (sales.partnerIqEnabled === true) return true
  return hasPartnerIqFeature(sales.features)
}

export const setLabraAiOnboardingComplete = (partnerId: string): void => {
  try {
    localStorage.setItem(
      `${LABRA_AI_ONBOARDING_COMPLETE_KEY}:${partnerId}`,
      'true'
    )
  } catch {
    // localStorage unavailable
  }
}

export const isLabraAiOnboardingCompleteLocal = (
  partnerId: string
): boolean => {
  try {
    return (
      localStorage.getItem(
        `${LABRA_AI_ONBOARDING_COMPLETE_KEY}:${partnerId}`
      ) === 'true'
    )
  } catch {
    return false
  }
}

/** Whether PartnerIQ was enabled in saved subscription payload. */
export const wasPartnerIqEnabledInForm = (subscribedProducts?: {
  SALES?: { features?: string[]; partnerIqEnabled?: boolean }
}): boolean => isPartnerIqOrgEnabled(subscribedProducts)

/** Map runtime PARTNERIQ back to admin API key PARTNER_IQ before save. */
export const getPartnerIqFeatureKeysForSave = <
  T extends { subscribedProducts: Record<string, unknown> },
>(
  customer: T
): T => {
  const sales = customer.subscribedProducts.SALES as
    | {
        features?: string[]
        partnerIqEnabled?: boolean
        [key: string]: unknown
      }
    | undefined
  if (!sales) return customer

  const partnerIqEnabled =
    sales.partnerIqEnabled !== false &&
    (sales.partnerIqEnabled === true || hasPartnerIqFeature(sales.features))

  let features = (sales.features ?? []).map(f =>
    f === PARTNER_IQ_FEATURE ? PARTNER_IQ_ADMIN_FEATURE : f
  )
  if (partnerIqEnabled) {
    if (!features.includes(PARTNER_IQ_ADMIN_FEATURE)) {
      features = [...features, PARTNER_IQ_ADMIN_FEATURE]
    }
  } else {
    features = features.filter(
      f => f !== PARTNER_IQ_ADMIN_FEATURE && f !== PARTNER_IQ_FEATURE
    )
  }

  const subscribedForAutoComplete = {
    ...customer.subscribedProducts,
    SALES: { ...sales, features, partnerIqEnabled },
  }

  let partnerIqOnboardingComplete: boolean | undefined
  if (!partnerIqEnabled) {
    partnerIqOnboardingComplete = false
  } else if (sales.partnerIqOnboardingComplete === true) {
    partnerIqOnboardingComplete = true
  } else if (
    isPartnerIqOrgEnabled(subscribedForAutoComplete) &&
    Boolean(customer.subscribedProducts.OPPSYNC) &&
    hasPropensityFeature(features) &&
    sales.partnerIqOnboardingComplete !== false
  ) {
    partnerIqOnboardingComplete = true
  } else if (sales.partnerIqOnboardingComplete === false) {
    partnerIqOnboardingComplete = false
  }

  return {
    ...customer,
    subscribedProducts: {
      ...customer.subscribedProducts,
      SALES: {
        ...sales,
        features,
        partnerIqEnabled,
        partnerIqOnboardingComplete,
      },
    },
  }
}

export const isSalesFeatureEnabled = (
  features: string[] | undefined,
  featureKey: string
): boolean => {
  const normalized = normalizeSalesFeatures(features ?? [])
  return normalized.includes(normalizeSalesFeatureKey(featureKey))
}

export const isSalesProductSubscribed = (subscribedProducts?: {
  SALES?: unknown
}): boolean => !!subscribedProducts?.SALES

export type SubscribedModuleKey = 'OPPSYNC' | 'FLYOUT' | 'CLOUDFAAS' | 'SALES'

type SubscribedProductsShape = {
  OPPSYNC?: unknown
  FLYOUT?: unknown
  CLOUDFAAS?: unknown
  SALES?: unknown
}

/** Partner has Labra AI only — no Co-sell, Marketplace, or CloudFaaS modules. */
export const isLabraAiOnlySubscription = (
  subscribedProducts?: SubscribedProductsShape
): boolean => {
  if (!isSalesProductSubscribed(subscribedProducts)) return false
  return (
    !subscribedProducts?.OPPSYNC &&
    !subscribedProducts?.FLYOUT &&
    !subscribedProducts?.CLOUDFAAS
  )
}

/** Labra AI–only org with PartnerIQ (no Co-sell module) — AWS setup uses ACE integration, not cloud picker. */
export const isPartnerIqOnlyLabraAiSubscription = (
  subscribedProducts?: SubscribedProductsShape & { SALES?: SalesSubscription }
): boolean =>
  isLabraAiOnlySubscription(subscribedProducts) &&
  isPartnerIqOrgEnabled(subscribedProducts)

export const isSubscribedToModule = (
  subscribedProducts: SubscribedProductsShape | undefined,
  module: SubscribedModuleKey
): boolean => {
  if (module === 'SALES') {
    return isSalesProductSubscribed(subscribedProducts)
  }
  return Boolean(subscribedProducts?.[module])
}

/**
 * Which top-level nav modules to show from admin subscription data.
 * Legacy partners with no subscribedProducts still see all modules unless partner-invite mode applies.
 */
export const resolveSubscribedModuleVisibility = (
  subscribedProducts: SubscribedProductsShape | undefined,
  partnerInviteEnabled: boolean
): Record<SubscribedModuleKey, boolean> => {
  const hasSubscriptionPayload =
    subscribedProducts !== undefined &&
    Object.keys(subscribedProducts).length > 0

  if (isLabraAiOnlySubscription(subscribedProducts)) {
    return {
      OPPSYNC: false,
      FLYOUT: false,
      CLOUDFAAS: false,
      SALES: true,
    }
  }

  if (partnerInviteEnabled || hasSubscriptionPayload) {
    return {
      OPPSYNC: isSubscribedToModule(subscribedProducts, 'OPPSYNC'),
      FLYOUT: isSubscribedToModule(subscribedProducts, 'FLYOUT'),
      CLOUDFAAS: isSubscribedToModule(subscribedProducts, 'CLOUDFAAS'),
      SALES: isSubscribedToModule(subscribedProducts, 'SALES'),
    }
  }

  return {
    OPPSYNC: true,
    FLYOUT: true,
    CLOUDFAAS: true,
    SALES: isSalesProductSubscribed(subscribedProducts),
  }
}

/** PartnerIQ nav + collab room — driven by admin subscription, not LD alone. */
export const isPartnerIqNavEnabled = (subscribedProducts?: {
  SALES?: { features?: string[]; partnerIqEnabled?: boolean }
}): boolean => isPartnerIqOrgEnabled(subscribedProducts)

/** PartnerIQ appears on the plan (enabled or explicitly disabled by admin). */
export const isPartnerIqInSubscription = (subscribedProducts?: {
  SALES?: { features?: string[]; partnerIqEnabled?: boolean }
}): boolean => {
  const sales = subscribedProducts?.SALES
  if (!sales) return false
  if (sales.partnerIqEnabled === true || sales.partnerIqEnabled === false) {
    return true
  }
  return hasPartnerIqFeature(sales.features ?? [])
}

/** Whether Labra AI side nav should include PartnerIQ (including admin-disabled). */
export const shouldShowPartnerIqInSideNav = isPartnerIqInSubscription

export const hasAnyLabraAiFeature = (features: string[] = []): boolean =>
  hasPropensityFeature(features) || hasPartnerIqFeature(features)
